import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import {
  getAllRpc,
  getAppRpc,
  type AppResponse as ChirpAppResponse,
  type ApplicationListingSimpleResponse,
  type AppsResponse as ChirpAppsResponse,
  type AuthInfo,
} from '../__generated__/chirp/com/hubspot/piesync/bridge/rpc/AppsRpc';
import {
  getDatasetParametersRpc,
  getRemoteAccountsRpc,
  deleteAccountRpc,
  verifyAccountsRpc,
  type AvailableDatasetParameter,
  type RemoteAccount,
  type VerifyAccountsResponse,
} from '../__generated__/chirp/com/hubspot/piesync/bridge/rpc/AccountsRpc';
import {
  createConnectionRpc,
  activateConnectionRpc,
  updateConnectionRpc,
  type SummarizedConnection,
} from '../__generated__/chirp/com/hubspot/piesync/bridge/rpc/ConnectionsRpc';

export const chirpClient = createRpcClientV2({
  hubHttpClient: http,
});

export function getApp(appId: number): Promise<AppResponse> {
  return chirpClient
    .call(getAppRpc, { request: { appId: Number(appId) } })
    .then(({ app }) => app);
}

export function listApps(): Promise<AppsResponse> {
  return chirpClient.call(getAllRpc, {});
}

export function postAccountDataset(
  accountId: number,
  __portalId: number
): Promise<Dataset[]> {
  return chirpClient
    .call(getDatasetParametersRpc, {
      request: {
        accountId: Number(accountId),
        datasetParameterRequest: {
          remoteObjectType: 'contacts',
          parameters: [],
        },
      },
    })
    .then(({ parameters }) =>
      parameters.map(
        (p: AvailableDatasetParameter): Dataset => ({
          available: p.available,
          name: p.name,
          options: p.options.map((o) => ({ label: o.label, value: o.value })),
        })
      )
    );
}

export function postAccountConnection(
  accountId: number,
  __portalId: number,
  __appId: number,
  scopes: ConnectionScopes[]
): Promise<Connection> {
  return chirpClient
    .call(createConnectionRpc, {
      request: {
        accountId: Number(accountId),
        scopesRequest: {
          objectTypes: {
            hubSpotObjectTypeId: '0-1',
            remote: 'contacts',
          },
          scopes: scopes.map((s) => ({
            name: s.name,
            label: s.label,
            value: s.value,
          })),
          direction: 'INBOUND',
        },
      },
    })
    .then(({ connection }: { connection: SummarizedConnection }) => ({
      id: connection.id,
      status: String(connection.status),
    }));
}

export function putConnection(
  connectionId: number,
  __portalId: number,
  __appId: number
): Promise<void> {
  return chirpClient
    .call(updateConnectionRpc, {
      request: {
        connectionId: Number(connectionId),
        settings: {
          connectionSettings: {
            conflictResolution: 'DEFAULT_TO_HUBSPOT',
            rules: {
              direction: 'INBOUND',
              possibleDirections: [],
              filters: {
                inboundFilters: [],
              },
              updateOnlyDirection: 'NONE',
            },
            matchingFilter: { name: 'email_only', value: true },
            defaultValueDatasetParameters: [],
            generalDatasetParameters: [],
            recordIdDatasetParameters: [],
            lastModifiedDatasetParameters: [],
            subscriptionSettingDatasetParameters: [],
          },
          fieldMappings: {
            mappings: [],
          },
        },
      },
    })
    .then(() => undefined);
}

export function postConnectionActivate(
  connectionId: number,
  __portalId: number,
  __appId: number
): Promise<void> {
  return chirpClient
    .call(activateConnectionRpc, {
      request: { connectionId: Number(connectionId) },
    })
    .then(() => undefined);
}

export interface Dataset {
  available: boolean;
  name: string;
  options: DatasetOption[];
}

export interface DatasetOption {
  label: string;
  value: string;
}

export interface Connection {
  id: number;
  status: string;
}

export interface ConnectionScopes extends DatasetOption {
  name: string;
}

export function listDataSyncAccounts(appId: number): Promise<RemoteAccount[]> {
  return chirpClient
    .call(getRemoteAccountsRpc, {
      request: { appId: Number(appId), includeDeleted: false },
    })
    .then(({ accounts }) => accounts);
}

export function deleteDataSyncAccount(accountId: number): Promise<void> {
  return chirpClient
    .call(deleteAccountRpc, { request: { accountId: Number(accountId) } })
    .then(() => undefined);
}

export function verifyDataSyncAccounts(
  accountIds: number[]
): Promise<VerifyAccountsResponse> {
  return chirpClient.call(verifyAccountsRpc, { request: { accountIds } });
}

export type { RemoteAccount, VerifyAccountsResponse };

export type { AuthInfo };

export type AppResponse = ChirpAppResponse;

export type ApplicationListing = ApplicationListingSimpleResponse;

export type AppsResponse = ChirpAppsResponse;
